import { lazy } from 'react'
import { Translation } from 'react-i18next'
import { Navigate, Outlet, Route } from 'react-router-dom'

import { typeUserViewScheduleEnum } from '@/modules/schedule/domain/models/TypeUserViewSchedule'
import SituationalIcon from '@/shared/components/icons/situational-icon'
import GalleryProvider from '@/therapist/contexts/GalleryContext'
import AbsentPatientIcon from '@/ui/assets/AbsentPatientIcon'
import BathroomHangerIcon from '@/ui/assets/BathroomHangerIcon'
import BodyInspectionIcon from '@/ui/assets/BodyInspectionIcon'
import CalendarPersonIcon from '@/ui/assets/CalendarPersonIcon'
import ChatMessageAddSquareIcon from '@/ui/assets/ChatMessageAddSquareIcon'
import ChatMessageIcon from '@/ui/assets/ChatMessageIcon'
import CommentsNewIcon from '@/ui/assets/CommentsNewIcon'
import DocumentPaperPenIcon from '@/ui/assets/DocumentPaperPenIcon'
// TEMP REMOVE REPORT
// import DocumentPaperPenIcon from '@/ui/assets/DocumentPaperPenIcon'
import FolderImageIcon from '@/ui/assets/FolderImageIcon'
import FoodIcon from '@/ui/assets/FoodIcon'
import FourSquareIcon from '@/ui/assets/FourSquareIcon'
import HealthPinIcon from '@/ui/assets/HealhPinIcon'
import HomeIcon from '@/ui/assets/HomeIcon'
import InterferingBehaviorIcon from '@/ui/assets/InterferingBehaviorIcon'
import LiquidIcon from '@/ui/assets/LiquidIcon'
import ManagementCollectionIcon from '@/ui/assets/ManagementCollectionIcon'
import MedicalCrossIcon from '@/ui/assets/MedicalCrossIcon'
import MedicalRecord from '@/ui/assets/MedicalRecord'
import MyGroupsIcon from '@/ui/assets/MyGroupsIcon'
import NotebookIcon from '@/ui/assets/NotebookIcon'
import OccurrenceIcon from '@/ui/assets/OccurrenceIcon'
import PersonDocumentSquareIcon from '@/ui/assets/PersonDocumentSquareIcon'
import PersonIcon from '@/ui/assets/PersonIcon'
import RemediesIcon from '@/ui/assets/RemediesIcon'
import SessionsIcon from '@/ui/assets/SessionsIcon'
import SleepStarCircleIcon from '@/ui/assets/SleepStarCircleIcon'
import TicketPaperIcon from '@/ui/assets/TicketPaperIcon'
import ToyIcon from '@/ui/assets/ToyIcon'
import UserSupportBalloonIcon from '@/ui/assets/UserSupportBalloonIcon'
import LocationBasedRemountComponent from '@/ui/components/LocationBasedRemountComponent'
import BelongItemsProvider from '@/ui/contexts/BelongsItemsContext'
import PatientProvider from '@/ui/contexts/PatientContext'
import { PresenceProvider } from '@/ui/contexts/PresenceContext'
import { ScheduleProvider } from '@/ui/contexts/ScheduleContext'
import TicketEvaluationProvider from '@/ui/contexts/TicketEvaluationContext'
import { ConfigClientModulesManager } from '@/ui/hoc/ConfigClientModules/ConfigClientModulesManager'
import { FeatureFlagManager } from '@/ui/hoc/FeatureFlag/FeatureFlagManager'
import { FeatureFlagManagerWithDefaultPage } from '@/ui/hoc/FeatureFlag/FeatureFlagManagerWithDefaultPage'
import { MultiSelectionProvider } from '@/ui/hoc/MultiSelection'
import { UserPermissionManager } from '@/ui/hoc/UserPermission/UserPermissionManager'
import UserPermissionShowComponent from '@/ui/hoc/UserPermission/UserPermissionShowComponent'
import RequestProvider from '@/ui/modules/operational/contexts/RequestTabsContext'
import {
  operationalDynamicRoutes,
  operationalRouteNames
} from '@/ui/routes/routesNames/operationalRouteNames'

import { TicketChatProvider } from '../contexts/TicketChatContext'
import { useUserPermission } from '../hooks/useUserPermission'
import FoodStockProvider from '../modules/operational/contexts/FoodStockContext'
import { BodyInspectionProvider } from '../modules/operational/views/BodyInspection/DailyTable/contexts/BodyInspectionFilterContext'
import CAA from '../modules/operational/views/CAA'
import OccurrencesProvider from '../modules/operational/views/Occurrences/context/OccurrencesContext'
import { MedicalRecordManagement } from '../modules/operational/views/ResponsibleMedicalRecord/MedicalRecordManagement'
import SessionsManagementProvider from '../modules/operational/views/SessionManager/contexts/SessionsManagementContext'

const PhotosPage = lazy(() => import('../modules/operational/views/Photos'))
const PhotosPatient = lazy(
  () => import('../modules/operational/views/Photos/PhotosPatient')
)
const InterferingBehavior = lazy(
  () => import('@/ui/modules/operational/views/InterferingBehavior')
)
const InterferingBehaviorPage = lazy(
  () =>
    import(
      '../modules/operational/views/InterferingBehavior/InterferingBehaviorPage'
    )
)
const ExploreAndInvitations = lazy(
  () =>
    import(
      '@/therapist/pages/groups/PostGroup/components/ExploreAndInvitations'
    )
)

const Occurrences = lazy(
  () => import('@/ui/modules/operational/views/Occurrences')
)
const Comments = lazy(() => import('@/ui/modules/operational/views/Comments'))
const CAAPatient = lazy(
  () => import('@/ui/modules/operational/views/CAAPatient')
)
const AgentTicket = lazy(
  () => import('../modules/operational/views/AgentTicket')
)
const AgentArea = lazy(
  () => import('../modules/operational/views/AgentTicket/AgentArea')
)
const CompletedAreaTicketList = lazy(
  () =>
    import(
      '../modules/operational/views/AgentTicket/AgentArea/TicketTab/CompletedAreaTicketList'
    )
)
const TicketDetails = lazy(
  () => import('../modules/operational/views/TicketDetails')
)
const UserTicket = lazy(() => import('../modules/operational/views/UserTicket'))

const GalleryUser = lazy(() => import('@/therapist/pages/galleryUser'))
const BodyInspection = lazy(
  () => import('@/ui/modules/operational/views/BodyInspection')
)
const ConfigClient = lazy(
  () => import('@/ui/modules/operational/views/ConfigClient')
)
const PatientTickets = lazy(
  () => import('@/ui/modules/operational/views/PatientTickets')
)
const Schedule = lazy(
  () => import('@/therapist/pages/patient-profile/schedule')
)
const BelongsManager = lazy(
  () => import('@/ui/modules/operational/views/BelongsManager')
)
const RemedyManager = lazy(
  () => import('@/ui/modules/operational/views/RemedyManager')
)
const Bathroom = lazy(() => import('@/ui/modules/operational/views/Bathroom'))
const Belongs = lazy(() => import('@/ui/modules/operational/views/Belongs'))
const SessionManager = lazy(
  () => import('@/ui/modules/operational/views/SessionManager')
)
const FrequenciesManager = lazy(
  () => import('@/ui/modules/operational/views/FrequenciesManager')
)
const PatientsAbsences = lazy(
  () => import('@/ui/modules/operational/views/AbsentPatients')
)
const FileEditor = lazy(() => import('@/ui/modules/file-editor/views'))
const Food = lazy(() => import('@operational-ui/views/Food'))
const Liquids = lazy(() => import('@/ui/modules/operational/views/Liquids'))
const PatientFoodReport = lazy(
  () => import('@/ui/modules/operational/views/PatientFoodReport')
)
const PatientLayout = lazy(() => import('@/ui/layouts/PatientLayout'))
const PatientSelector = lazy(() => import('@ui/pages/PatientSelector'))
const Request = lazy(() => import('@/ui/modules/operational/views/Requests'))
const Restriction = lazy(
  () => import('@/ui/modules/operational/views/Restriction')
)
const Sleep = lazy(() => import('@/ui/modules/operational/views/Sleep'))
const Remedy = lazy(() => import('@/ui/modules/operational/views/Remedy'))
const InternalLayout = lazy(() => import('@ui/layouts/InternalLayout'))
const FoodScheduleProvider = lazy(
  () => import('../modules/operational/contexts/FoodScheduleTabsContext')
)
const Group = lazy(() => import('@/therapist/pages/groups'))
// TEMP REMOVE REPORT
// const DocumentsPage = lazy(() => import('@/therapist/pages/documents'))
const GroupFeed = lazy(
  () => import('@/ui/modules/operational/views/GroupsFeed')
)
const PostDetails = lazy(() => import('@/therapist/pages/post-details'))
const Chat = lazy(() => import('@/therapist/pages/chat'))
const TicketChat = lazy(() => import('@/ui/modules/shared/TicketChat'))
const ProfileInfo = lazy(() => import('@/shared/pages/client'))
const Gallery = lazy(() => import('@/therapist/pages/patient-profile/gallery'))
const Profile = lazy(() => import('@/therapist/pages/profile'))
const TaskPage = lazy(() => import('@/therapist/pages/tasks'))
const MedicalRecordPatient = lazy(
  () => import('@/therapist/pages/medicalRecord')
)

export function OperationalRoutes() {
  const { checkPermission } = useUserPermission()

  const patientGalleryRoutes = (
    <>
      <Route
        path={operationalRouteNames.gallery}
        element={
          <UserPermissionShowComponent permission="DRIVE_VIEW">
            <MultiSelectionProvider>
              <GalleryProvider>
                <Gallery />
              </GalleryProvider>
            </MultiSelectionProvider>
          </UserPermissionShowComponent>
        }
      />
      <Route
        path={operationalRouteNames.galleryItem}
        element={
          <UserPermissionShowComponent permission="DRIVE_VIEW">
            <MultiSelectionProvider>
              <GalleryProvider>
                <Gallery />
              </GalleryProvider>
            </MultiSelectionProvider>
          </UserPermissionShowComponent>
        }
      />
      <Route
        path={operationalRouteNames.galleryFolder}
        element={
          <UserPermissionShowComponent permission="DRIVE_VIEW">
            <MultiSelectionProvider>
              <GalleryProvider>
                <Gallery />
              </GalleryProvider>
            </MultiSelectionProvider>
          </UserPermissionShowComponent>
        }
      />
    </>
  )

  return (
    <>
      <Route
        element={
          <PresenceProvider>
            <TicketChatProvider>
              <TicketEvaluationProvider>
                <PatientProvider>
                  <Outlet />
                </PatientProvider>
              </TicketEvaluationProvider>
            </TicketChatProvider>
          </PresenceProvider>
        }
      >
        <Route
          path={operationalRouteNames.home}
          element={<Navigate to={operationalRouteNames.groups} replace />}
        />

        <Route
          element={
            <PatientProvider>
              <Translation>
                {(t) => (
                  <FeatureFlagManager>
                    {({ checkFlagAndReturn }) => (
                      <UserPermissionManager>
                        {({
                          checkPermissionAndReturn,
                          checkNotPermissionAndReturn
                        }) => (
                          <InternalLayout
                            homeLink={operationalRouteNames.groups}
                            menuItems={[
                              {
                                name: t('internalLayout.general.home'),
                                icon: <HomeIcon />,
                                navigateTo: operationalRouteNames.groups
                              },
                              {
                                name: 'Gestão',
                                icon: <ManagementCollectionIcon />,
                                subMenu: [
                                  {
                                    name: t(
                                      'internalLayout.operational.absences'
                                    ),
                                    icon: <AbsentPatientIcon />,
                                    navigateTo:
                                      operationalRouteNames.patientsAbsences
                                  },
                                  {
                                    name: t('internalLayout.operational.caa'),
                                    icon: <FourSquareIcon />,
                                    navigateTo: operationalRouteNames.caa
                                  },
                                  {
                                    name: t(
                                      'internalLayout.operational.interferingBehavior'
                                    ),
                                    icon: <InterferingBehaviorIcon />,
                                    navigateTo:
                                      operationalRouteNames.interferingBehavior
                                  },
                                  {
                                    name: t(
                                      'internalLayout.operational.frequenciesManager'
                                    ),
                                    icon: <HealthPinIcon />,
                                    navigateTo:
                                      operationalRouteNames.frequenciesManager
                                  },
                                  ...checkFlagAndReturn('MEDICINES', {
                                    name: t(
                                      'internalLayout.operational.remedyManager'
                                    ),
                                    icon: <MedicalCrossIcon />,
                                    navigateTo:
                                      operationalRouteNames.remedyManager
                                  }),
                                  {
                                    name: t(
                                      'internalLayout.operational.occurrences'
                                    ),
                                    icon: <OccurrenceIcon />,
                                    navigateTo:
                                      operationalRouteNames.occurrences
                                  },
                                  ...checkFlagAndReturn('FOOD', {
                                    name: t(
                                      'internalLayout.operational.requests'
                                    ),
                                    icon: <FoodIcon />,
                                    navigateTo: operationalRouteNames.request
                                  }),
                                  ...checkFlagAndReturn('BELONGINGS', {
                                    name: t(
                                      'internalLayout.operational.belongManager'
                                    ),
                                    icon: <ToyIcon />,
                                    navigateTo:
                                      operationalRouteNames.belongManager
                                  }),
                                  ...checkPermissionAndReturn(
                                    'SESSION_MANAGEMENT',
                                    {
                                      name: t(
                                        'internalLayout.operational.sessions'
                                      ),
                                      icon: <SessionsIcon />,
                                      navigateTo: operationalRouteNames.session
                                    }
                                  )
                                ]
                              },
                              ...checkPermissionAndReturn('PATIENT_LIST_VIEW', {
                                name: t('internalLayout.operational.patients'),
                                icon: <PersonIcon />,
                                navigateTo: operationalRouteNames.patients
                              }),
                              {
                                name: t('internalLayout.operational.ticket'),
                                icon: <TicketPaperIcon />,
                                subMenu: [
                                  ...checkPermissionAndReturn(
                                    'MANAGE_MEDICAL_RECORD',
                                    {
                                      name: t(
                                        'internalLayout.operational.medicalRecordManagement'
                                      ),
                                      icon: <PersonDocumentSquareIcon />,
                                      navigateTo:
                                        operationalRouteNames.medicalRecordManagement
                                    }
                                  ),
                                  {
                                    name: t(
                                      'internalLayout.operational.agentTicket'
                                    ),
                                    icon: <UserSupportBalloonIcon />,
                                    navigateTo:
                                      operationalRouteNames.agentTicket
                                  },
                                  {
                                    name: t(
                                      'internalLayout.operational.openTicket'
                                    ),
                                    icon: <ChatMessageAddSquareIcon />,
                                    navigateTo: operationalRouteNames.ticket
                                  },
                                  {
                                    name: t('internalLayout.operational.tasks'),
                                    icon: <NotebookIcon />,
                                    navigateTo: operationalRouteNames.tasks
                                  }
                                ]
                              }
                            ]}
                            searchItems={[
                              {
                                name: 'Feed',
                                icon: <HomeIcon />,
                                route: operationalRouteNames.groupDetails,
                                isNavigatable: false,
                                isSearchable: false,
                                children: [
                                  {
                                    name: 'Drive do grupo',
                                    icon: <HomeIcon />,
                                    route: operationalRouteNames.groupDrive,
                                    isSearchable: false,
                                    isNavigatable: false
                                  }
                                ]
                              },
                              {
                                name: t('internalLayout.general.home'),
                                icon: <HomeIcon />,
                                route: operationalRouteNames.groups,
                                children: [
                                  {
                                    name: 'Explorar e convites',
                                    route: operationalRouteNames.explore,
                                    module: t('internalLayout.general.home')
                                  }
                                ]
                              },
                              {
                                name: 'Gestão',
                                icon: <ManagementCollectionIcon />,
                                children: [
                                  {
                                    name: t(
                                      'internalLayout.operational.absences'
                                    ),
                                    icon: <AbsentPatientIcon />,
                                    route:
                                      operationalRouteNames.patientsAbsences,
                                    module: 'Gestão'
                                  },
                                  {
                                    name: 'Gerenciamento de frequências',
                                    icon: <PersonIcon />,
                                    route:
                                      operationalRouteNames.frequenciesManager,
                                    module: 'Gestão'
                                  },
                                  ...checkFlagAndReturn('MEDICINES', {
                                    name: t(
                                      'internalLayout.operational.remedyManager'
                                    ),
                                    icon: <MedicalCrossIcon />,
                                    route: operationalRouteNames.remedyManager
                                  }),
                                  {
                                    name: t(
                                      'internalLayout.operational.occurrences'
                                    ),
                                    icon: <OccurrenceIcon />,
                                    route: operationalRouteNames.occurrences,
                                    module: 'Gestão'
                                  },
                                  // ...checkPermissionAndReturn(
                                  //   'REVIEW_MEDIA_TO_RESPONSIBLE',
                                  //   {
                                  //     name: t(
                                  //       'internalLayout.operational.mediaSharing'
                                  //     ),
                                  //     icon: <CameraIcon />,
                                  //     route: operationalRouteNames.photos,
                                  //     module: 'Gestão'
                                  //   }
                                  // ),
                                  {
                                    name: t(
                                      'internalLayout.operational.interferingBehavior'
                                    ),
                                    icon: <InterferingBehaviorIcon />,
                                    route:
                                      operationalRouteNames.interferingBehavior,
                                    module: 'Gestão'
                                  },
                                  {
                                    name: t('internalLayout.operational.caa'),
                                    icon: <FourSquareIcon />,
                                    route: operationalRouteNames.caa,
                                    module: 'Gestão'
                                  },
                                  ...checkFlagAndReturn('FOOD', {
                                    name: 'Gestão de pedidos',
                                    icon: <FoodIcon />,
                                    route: operationalRouteNames.request,
                                    module: 'Gestão'
                                  }),
                                  ...checkFlagAndReturn('BELONGINGS', {
                                    name: t(
                                      'internalLayout.operational.belongManager'
                                    ),
                                    icon: <ToyIcon />,
                                    route: operationalRouteNames.belongManager,
                                    module: 'Gestão'
                                  }),
                                  {
                                    name: t(
                                      'internalLayout.operational.sessions'
                                    ),
                                    icon: <SessionsIcon />,
                                    route: operationalRouteNames.session,
                                    module: 'Gestão'
                                  }
                                ]
                              },
                              ...checkNotPermissionAndReturn(
                                'PATIENT_LIST_VIEW',
                                {
                                  name: t(
                                    'internalLayout.operational.patients'
                                  ),
                                  icon: <PersonIcon />,
                                  route: operationalRouteNames.patients,
                                  isSearchable: false,
                                  isNavigatable: false,
                                  children: [
                                    {
                                      name: t('patient.sidebar.items.gallery'),
                                      icon: <FolderImageIcon />,
                                      route: operationalRouteNames.gallery,
                                      isSearchable: false,
                                      isNavigatable: false
                                    }
                                  ]
                                }
                              ),
                              ...checkPermissionAndReturn('PATIENT_LIST_VIEW', {
                                name: t('internalLayout.operational.patients'),
                                icon: <PersonIcon />,
                                route: operationalRouteNames.patients,
                                children: [
                                  {
                                    name: t('patient.sidebar.items.schedule'),
                                    icon: <CalendarPersonIcon />,
                                    route: operationalRouteNames.clientSchedule,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.food'),
                                    icon: <FoodIcon />,
                                    isSearchable: false,
                                    isNavigatable: false,
                                    children: [
                                      {
                                        name: t(
                                          'patient.sidebar.items.foodReport'
                                        ),
                                        route: operationalRouteNames.foodReport,
                                        module: t('patient.sidebar.items.food'),
                                        isSearchable: false,
                                        isNavigatable: false
                                      },
                                      {
                                        name: t(
                                          'patient.sidebar.items.scheduleFood'
                                        ),
                                        route: operationalRouteNames.food,
                                        module: t('patient.sidebar.items.food'),
                                        isSearchable: false,
                                        isNavigatable: false
                                      },
                                      {
                                        name: t(
                                          'patient.sidebar.items.restrictionFood'
                                        ),
                                        route:
                                          operationalRouteNames.restriction,
                                        module: t('patient.sidebar.items.food'),
                                        isSearchable: false,
                                        isNavigatable: false
                                      }
                                    ]
                                  },
                                  {
                                    name: t('patient.sidebar.items.bathroom'),
                                    icon: <BathroomHangerIcon />,
                                    route: operationalRouteNames.bathroom,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.caa'),
                                    icon: <FourSquareIcon />,
                                    route: operationalRouteNames.caaClient,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  // {
                                  //   name: t(
                                  //     'internalLayout.operational.mediaSharing'
                                  //   ),
                                  //   icon: <CameraIcon />,
                                  //   route: operationalRouteNames.photosClient,
                                  //   isSearchable: false,
                                  //   isNavigatable: false
                                  // },
                                  {
                                    name: t(
                                      'patient.sidebar.items.interferingBehavior'
                                    ),
                                    icon: <InterferingBehaviorIcon />,
                                    route:
                                      operationalRouteNames.interferingBehaviorClient,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.tickets'),
                                    icon: <TicketPaperIcon />,
                                    route: operationalRouteNames.patientTicket,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.comments'),
                                    icon: <CommentsNewIcon />,
                                    route: operationalRouteNames.comments,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t(
                                      'patient.sidebar.items.configClient'
                                    ),
                                    icon: <SituationalIcon />,
                                    route: operationalRouteNames.configClient,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  // TEMP REMOVE REPORT
                                  // {
                                  //   name: t('patient.sidebar.items.documents'),
                                  //   icon: <DocumentPaperPenIcon />,
                                  //   route: operationalRouteNames.documents,
                                  //   isSearchable: false,
                                  //   isNavigatable: false
                                  // },
                                  {
                                    name: t('patient.sidebar.items.gallery'),
                                    icon: <FolderImageIcon />,
                                    route: operationalRouteNames.gallery,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t(
                                      'patient.sidebar.items.medicalRecord'
                                    ),
                                    icon: <MedicalRecord />,
                                    route: operationalRouteNames.medicalSheet,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.myGroups'),
                                    icon: <MyGroupsIcon />,
                                    route: operationalRouteNames.groupsFeed,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.liquids'),
                                    icon: <LiquidIcon />,
                                    route: operationalRouteNames.liquids,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.remedy'),
                                    icon: <RemediesIcon />,
                                    route: operationalRouteNames.remedy,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.belongs'),
                                    icon: <ToyIcon />,
                                    route: operationalRouteNames.patientBelongs,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t('patient.sidebar.items.sleep'),
                                    icon: <SleepStarCircleIcon />,
                                    route: operationalRouteNames.sleep,
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t(
                                      'patient.sidebar.items.bodyInspection'
                                    ),
                                    icon: <BodyInspectionIcon />,
                                    route: operationalRouteNames.bodyInspection,
                                    isSearchable: false,
                                    isNavigatable: false
                                  }
                                ]
                              }),
                              {
                                name: t('internalLayout.therapist.ticket'),
                                children: [
                                  ...checkPermissionAndReturn(
                                    'MANAGE_MEDICAL_RECORD',
                                    {
                                      name: t(
                                        'internalLayout.operational.medicalRecordManagement'
                                      ),
                                      icon: <PersonDocumentSquareIcon />,
                                      route:
                                        operationalRouteNames.medicalRecordManagement,
                                      isSearchable: false,
                                      isNavigatable: false
                                    }
                                  ),
                                  {
                                    name: 'Detalhes do chamado',
                                    icon: <ChatMessageAddSquareIcon />,
                                    route: operationalRouteNames.ticketDetails,
                                    module: t(
                                      'internalLayout.therapist.ticket'
                                    ),
                                    isSearchable: false,
                                    isNavigatable: false
                                  },
                                  {
                                    name: t(
                                      'internalLayout.therapist.openTicket'
                                    ),
                                    icon: <ChatMessageAddSquareIcon />,
                                    route: operationalRouteNames.ticket,
                                    module: t('internalLayout.therapist.ticket')
                                  },
                                  {
                                    name: t('internalLayout.therapist.tasks'),
                                    icon: <NotebookIcon />,
                                    route: operationalRouteNames.tasks,
                                    module: t('internalLayout.therapist.ticket')
                                  },
                                  {
                                    name: 'Chat de chamados',
                                    icon: <NotebookIcon />,
                                    route: operationalRouteNames.ticketChat,
                                    module: t('internalLayout.therapist.ticket')
                                  },
                                  {
                                    name: t(
                                      'internalLayout.operational.agentTicket'
                                    ),
                                    icon: <UserSupportBalloonIcon />,
                                    route: operationalRouteNames.agentTicket,
                                    module: t(
                                      'internalLayout.therapist.ticket'
                                    ),
                                    children: [
                                      {
                                        name: 'Área de chamado',
                                        icon: <UserSupportBalloonIcon />,
                                        route:
                                          operationalRouteNames.agentTicketArea,
                                        module: t(
                                          'internalLayout.therapist.ticket'
                                        ),
                                        isNavigatable: false,
                                        isSearchable: false,
                                        children: [
                                          {
                                            name: 'Chamados concluídos',
                                            icon: <UserSupportBalloonIcon />,
                                            route:
                                              operationalRouteNames.agentTicketAreaCompleted,
                                            module: t(
                                              'internalLayout.therapist.ticket'
                                            ),
                                            isNavigatable: false,
                                            isSearchable: false
                                          }
                                        ]
                                      }
                                    ]
                                  }
                                ]
                              },
                              {
                                name: 'Perfil',
                                icon: <PersonIcon />,
                                route: operationalRouteNames.profileInfo,
                                isSearchable: false,
                                isNavigatable: false
                              },
                              {
                                name: 'Meu perfil',
                                icon: <PersonIcon />,
                                route: operationalRouteNames.profile
                              },
                              {
                                name: 'Chat',
                                icon: <ChatMessageIcon />,
                                route: operationalRouteNames.chat
                              },
                              {
                                name: 'Editor de arquivo',
                                icon: <DocumentPaperPenIcon />,
                                route: operationalRouteNames.fileEditor
                              },
                              {
                                name: 'Detalhes da publicação',
                                icon: <HomeIcon />,
                                route: operationalRouteNames.postDetails,
                                isSearchable: false,
                                isNavigatable: false
                              }
                            ]}
                          />
                        )}
                      </UserPermissionManager>
                    )}
                  </FeatureFlagManager>
                )}
              </Translation>
            </PatientProvider>
          }
        >
          <Route path={operationalRouteNames.ticket} element={<UserTicket />} />
          <Route
            path={operationalRouteNames.agentTicket}
            element={<AgentTicket />}
          />
          <Route
            path={operationalRouteNames.agentTicketAreaCompleted}
            element={<CompletedAreaTicketList />}
          />
          <Route
            path={operationalRouteNames.agentTicketArea}
            element={<AgentArea />}
          />
          <Route
            path={operationalRouteNames.medicalRecordManagement}
            element={<MedicalRecordManagement />}
          />
          <Route
            path={operationalRouteNames.ticketDetails}
            element={<TicketDetails />}
          />
          <Route path={operationalRouteNames.profile} element={<Profile />} />
          <Route
            path={operationalRouteNames.session}
            element={
              <SessionsManagementProvider>
                <SessionManager />
              </SessionsManagementProvider>
            }
          />
          <Route
            path={operationalRouteNames.explore}
            element={<ExploreAndInvitations />}
          />
          <Route
            path={operationalRouteNames.frequenciesManager}
            element={
              <FeatureFlagManagerWithDefaultPage
                flags={['LIQUIDS', 'BATHROOM', 'SLEEP']}
              >
                <FrequenciesManager />
              </FeatureFlagManagerWithDefaultPage>
            }
          />
          <Route
            path={operationalRouteNames.patientsAbsences}
            element={<PatientsAbsences />}
          />
          <Route
            path={operationalRouteNames.profileInfo}
            element={<ProfileInfo />}
          />
          <Route
            path={operationalRouteNames.postDetails}
            element={<PostDetails />}
          />
          <Route
            path={operationalRouteNames.ticketChat}
            element={<TicketChat />}
          />
          <Route path={operationalRouteNames.chat} element={<Chat />} />
          <Route
            path={operationalRouteNames.fileEditor}
            element={<FileEditor />}
          />
          <Route
            path={operationalRouteNames.groups}
            element={
              <LocationBasedRemountComponent stableParams={['filter']}>
                <Group />
              </LocationBasedRemountComponent>
            }
          />
          <Route
            path={operationalRouteNames.occurrences}
            element={
              <OccurrencesProvider>
                <Occurrences />
              </OccurrencesProvider>
            }
          />
          <Route path={operationalRouteNames.caa} element={<CAA />} />
          <Route
            path={operationalRouteNames.interferingBehavior}
            element={<InterferingBehaviorPage />}
          />
          <Route path={operationalRouteNames.photos} element={<PhotosPage />} />
          <Route path={operationalRouteNames.tasks} element={<TaskPage />} />
          <Route
            path={operationalRouteNames.taskDetails}
            element={<TaskPage />}
          />
          <Route
            element={
              <RequestProvider>
                <Outlet />
              </RequestProvider>
            }
          >
            <Route path={operationalRouteNames.request} element={<Request />} />
          </Route>
          <Route
            path={operationalRouteNames.patients}
            element={<PatientSelector personaType="OPERATIONAL" />}
          />
          <Route
            path={operationalRouteNames.belongManager}
            element={
              <FeatureFlagManagerWithDefaultPage flags={['BELONGINGS']}>
                <BelongsManager />
              </FeatureFlagManagerWithDefaultPage>
            }
          />
          <Route
            path={operationalRouteNames.remedyManager}
            element={
              <FeatureFlagManagerWithDefaultPage flags={['MEDICINES']}>
                <RemedyManager />
              </FeatureFlagManagerWithDefaultPage>
            }
          />

          {checkPermission('PATIENT_LIST_VIEW') ? <></> : patientGalleryRoutes}
          <Route
            element={
              <Translation>
                {(t) => (
                  <FeatureFlagManager>
                    {({ checkFlag }) => (
                      <ConfigClientModulesManager>
                        {({ checkPermissionModulesAndReturn }) => (
                          <UserPermissionManager>
                            {({ checkPermissionAndReturn }) => (
                              <PatientLayout
                                menuItems={(patientId) => [
                                  ...checkPermissionAndReturn('AGENDA_VIEW', {
                                    name: t('patient.sidebar.items.schedule'),
                                    icon: (
                                      <CalendarPersonIcon fontSize={'24'} />
                                    ),
                                    navigateTo:
                                      operationalDynamicRoutes.schedule(
                                        patientId
                                      )
                                  }),
                                  ...(checkFlag('FOOD')
                                    ? checkPermissionModulesAndReturn('FOOD', {
                                        name: t('patient.sidebar.items.food'),
                                        icon: <FoodIcon fontSize={'24'} />,
                                        subMenu: [
                                          {
                                            name: t(
                                              'patient.sidebar.items.scheduleFood'
                                            ),
                                            navigateTo:
                                              operationalDynamicRoutes.food(
                                                patientId
                                              )
                                          },
                                          {
                                            name: t(
                                              'patient.sidebar.items.foodReport'
                                            ),
                                            navigateTo:
                                              operationalDynamicRoutes.foodReport(
                                                patientId
                                              )
                                          },
                                          {
                                            name: t(
                                              'patient.sidebar.items.restrictionFood'
                                            ),
                                            navigateTo:
                                              operationalDynamicRoutes.restriction(
                                                patientId
                                              )
                                          }
                                        ]
                                      })
                                    : []),
                                  ...(checkFlag('BATHROOM')
                                    ? checkPermissionModulesAndReturn(
                                        'BATHROOM',
                                        {
                                          name: t(
                                            'patient.sidebar.items.bathroom'
                                          ),
                                          icon: (
                                            <BathroomHangerIcon
                                              fontSize={'24'}
                                            />
                                          ),
                                          navigateTo:
                                            operationalDynamicRoutes.bathroom(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...checkPermissionModulesAndReturn('CAA', {
                                    name: t('patient.sidebar.items.caa'),
                                    icon: <FourSquareIcon />,
                                    navigateTo:
                                      operationalDynamicRoutes.caaClient(
                                        patientId
                                      )
                                  }),
                                  {
                                    name: t(
                                      'patient.sidebar.items.interferingBehavior'
                                    ),
                                    icon: (
                                      <InterferingBehaviorIcon
                                        fontSize={'24'}
                                      />
                                    ),
                                    navigateTo:
                                      operationalDynamicRoutes.interferingBehavior(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t('patient.sidebar.items.tickets'),
                                    icon: <TicketPaperIcon fontSize={'24'} />,
                                    navigateTo:
                                      operationalDynamicRoutes.patientTicket(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t('patient.sidebar.items.comments'),
                                    icon: <CommentsNewIcon />,
                                    navigateTo:
                                      operationalDynamicRoutes.comments(
                                        patientId
                                      )
                                  },
                                  ...checkPermissionAndReturn(
                                    'MANAGER_DAILY_ACCOMPANIMENT',
                                    {
                                      name: t(
                                        'patient.sidebar.items.configClient'
                                      ),
                                      icon: <SituationalIcon fontSize={'24'} />,
                                      navigateTo:
                                        operationalDynamicRoutes.config(
                                          patientId
                                        )
                                    }
                                  ),
                                  ...checkPermissionAndReturn('DRIVE_VIEW', {
                                    name: t('patient.sidebar.items.gallery'),
                                    icon: <FolderImageIcon fontSize={'24'} />,
                                    navigateTo:
                                      operationalDynamicRoutes.gallery(
                                        patientId
                                      )
                                  }),
                                  {
                                    name: t(
                                      'patient.sidebar.items.medicalRecord'
                                    ),
                                    icon: <MedicalRecord fontSize={'24'} />,
                                    navigateTo:
                                      operationalDynamicRoutes.medicalSheet(
                                        patientId
                                      )
                                  },
                                  {
                                    name: t('patient.sidebar.items.myGroups'),
                                    icon: (
                                      <MyGroupsIcon
                                        color="#077FDE"
                                        fontSize={'24'}
                                      />
                                    ),
                                    navigateTo:
                                      operationalDynamicRoutes.groupsFeed(
                                        patientId
                                      )
                                  },
                                  ...(checkFlag('LIQUIDS')
                                    ? checkPermissionModulesAndReturn(
                                        'LIQUIDS',
                                        {
                                          name: t(
                                            'patient.sidebar.items.liquids'
                                          ),
                                          icon: <LiquidIcon fontSize={'24'} />,
                                          navigateTo:
                                            operationalDynamicRoutes.liquids(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...(checkFlag('MEDICINES')
                                    ? checkPermissionModulesAndReturn(
                                        'MEDICINES',
                                        {
                                          name: t(
                                            'patient.sidebar.items.remedy'
                                          ),
                                          icon: (
                                            <RemediesIcon fontSize={'24'} />
                                          ),
                                          navigateTo:
                                            operationalDynamicRoutes.remedy(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...(checkFlag('BELONGINGS')
                                    ? checkPermissionModulesAndReturn(
                                        'BELONGINGS',
                                        {
                                          name: t(
                                            'patient.sidebar.items.belongs'
                                          ),
                                          icon: <ToyIcon fontSize={'24'} />,
                                          navigateTo:
                                            operationalDynamicRoutes.patientBelongs(
                                              patientId
                                            )
                                        }
                                      )
                                    : []),
                                  ...(checkFlag('SLEEP')
                                    ? checkPermissionModulesAndReturn('SLEEP', {
                                        name: t('patient.sidebar.items.sleep'),
                                        icon: (
                                          <SleepStarCircleIcon
                                            fontSize={'24'}
                                          />
                                        ),
                                        navigateTo:
                                          operationalDynamicRoutes.sleep(
                                            patientId
                                          )
                                      })
                                    : []),
                                  ...(checkFlag('LESION')
                                    ? checkPermissionModulesAndReturn(
                                        'LESION',
                                        {
                                          name: t(
                                            'patient.sidebar.items.bodyInspection'
                                          ),
                                          icon: (
                                            <BodyInspectionIcon
                                              fontSize={'24'}
                                            />
                                          ),
                                          navigateTo:
                                            operationalDynamicRoutes.bodyInspection(
                                              patientId
                                            )
                                        }
                                      )
                                    : [])
                                ]}
                              />
                            )}
                          </UserPermissionManager>
                        )}
                      </ConfigClientModulesManager>
                    )}
                  </FeatureFlagManager>
                )}
              </Translation>
            }
          >
            <Route
              path={operationalRouteNames.food}
              element={
                <FoodScheduleProvider>
                  <FoodStockProvider>
                    <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                      <Food />
                    </FeatureFlagManagerWithDefaultPage>
                  </FoodStockProvider>
                </FoodScheduleProvider>
              }
            />
            <Route
              path={operationalRouteNames.restriction}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                  <Restriction />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.clientSchedule}
              element={
                <UserPermissionShowComponent permission="AGENDA_VIEW">
                  <ScheduleProvider>
                    <Schedule
                      userViewType={typeUserViewScheduleEnum.enum.PATIENT}
                    />
                  </ScheduleProvider>
                </UserPermissionShowComponent>
              }
            />
            <Route
              path={operationalRouteNames.foodReport}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['FOOD']}>
                  <PatientFoodReport />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.medicalSheet}
              element={<MedicalRecordPatient />}
            />
            {patientGalleryRoutes}
            <Route
              path={operationalRouteNames.liquids}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['LIQUIDS']}>
                  <Liquids />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.bathroom}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['BATHROOM']}>
                  <Bathroom />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.caaClient}
              element={<CAAPatient />}
            />
            <Route
              path={operationalRouteNames.photosClient}
              element={<PhotosPatient />}
            />
            <Route
              path={operationalRouteNames.sleep}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['SLEEP']}>
                  <Sleep />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.interferingBehaviorClient}
              element={<InterferingBehavior />}
            />
            <Route
              path={operationalRouteNames.patientBelongs}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['BELONGINGS']}>
                  <BelongItemsProvider>
                    <Belongs />
                  </BelongItemsProvider>
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.comments}
              element={<Comments />}
            />
            <Route
              path={operationalRouteNames.patientTicket}
              element={<PatientTickets />}
            />
            <Route
              path={operationalRouteNames.remedy}
              element={
                <FeatureFlagManagerWithDefaultPage flags={['MEDICINES']}>
                  <Remedy />
                </FeatureFlagManagerWithDefaultPage>
              }
            />
            <Route
              path={operationalRouteNames.configClient}
              element={<ConfigClient />}
            />
            <Route
              path={operationalRouteNames.bodyInspection}
              element={
                <BodyInspectionProvider>
                  <BodyInspection />
                </BodyInspectionProvider>
              }
            />
            <Route
              path={operationalRouteNames.groupsFeed}
              element={<GroupFeed />}
            />
            <Route path={operationalRouteNames.entryPatient} element={<></>} />
          </Route>

          <Route
            path={operationalRouteNames.groupDrive}
            element={
              <MultiSelectionProvider>
                <GalleryProvider>
                  <GalleryUser />
                </GalleryProvider>
              </MultiSelectionProvider>
            }
          />
          <Route
            path={operationalRouteNames.groupDriveFolder}
            element={
              <MultiSelectionProvider>
                <GalleryProvider>
                  <GalleryUser />
                </GalleryProvider>
              </MultiSelectionProvider>
            }
          />
          <Route
            path={operationalRouteNames.groupDriveItem}
            element={
              <MultiSelectionProvider>
                <GalleryProvider>
                  <GalleryUser />
                </GalleryProvider>
              </MultiSelectionProvider>
            }
          />
        </Route>
      </Route>
    </>
  )
}
